/**
 * 格口类型枚举
 */
export enum ChuteType {
  NORMAL = 0,
  /** 异常格口（原「业务异常」，两类化后统一为异常，承接的异常原因由 exceptionCodes 指定；原非业务异常(2)已刷数归并） */
  EXCEPTION = 1,
  DISABLED = 3,
}

/** 是否异常格口（严格模式：旧非业务异常(2)已刷数归并，不再兼容） */
export const isExceptionChuteType = (type: number | null | undefined): boolean =>
  type === ChuteType.EXCEPTION;

/** 未分配的异常类型（异常件兜底格口标记） */
export const UNASSIGNED_EXCEPTION_CODE = 'ANY';

/** 异常原因码（与后端 SorterExceptionCode 对齐）；「未分配」排首位——它是异常格口的默认值与最常用项 */
export const exceptionCodeOptions = [
  UNASSIGNED_EXCEPTION_CODE, 'E01', 'E02', 'E03', 'E11', 'E12', 'E13', 'E14', 'E15', 'E21', 'E22', 'E23', 'E24', 'E81', 'E82', 'E99',
].map(code => ({ value: code, labelKey: `wms.sorterPlan.exceptionCode.${code}` }));

/**
 * 业务类型枚举
 */
export enum BusinessType {
  SWIFTX = 1,
  TT = 2,
  TEMU = 3,
}

/** TEMU 格口允许的尾程商 carrierCode（FinalMileCarrier.value）：USPS=1 走 GA/SCF 分段规则，DHLe=26 走单段目的地 */
export const TEMU_CARRIER_USPS = 1;
export const TEMU_CARRIER_DHLE = 26;
export const TEMU_ALLOWED_CARRIER_CODES: readonly number[] = [TEMU_CARRIER_USPS, TEMU_CARRIER_DHLE];

/**
 * 打印类型枚举
 */
export enum PrintType {
  SWIFTX = 1,
  TT = 2,
}

/**
 * TT业务类型枚举（与后端 TtBagBizType 枚举名、DB 列 tt_biz_type 存储值一致）
 * UNLIMITED 表示"不限"，直接提交存库；存量/未配置行为 null，同样按"不限"渲染
 */
export enum TtBizType {
  UNLIMITED = 'UNLIMITED',
  FBT = 'FBT',
  CBT = 'CBT',
}

/**
 * TT业务类型选项
 */
export const ttBizTypeOptions = [
  { value: TtBizType.UNLIMITED, labelKey: 'wms.sorterPlan.ttBusinessType.unlimited' },
  { value: TtBizType.FBT, labelKey: 'wms.sorterPlan.ttBusinessType.fbt' },
  { value: TtBizType.CBT, labelKey: 'wms.sorterPlan.ttBusinessType.cbt' },
];

/**
 * 格口类型选项
 */
export const chuteTypeOptions = [
  { value: ChuteType.NORMAL, labelKey: 'wms.sorterPlan.chuteType.normal' },
  { value: ChuteType.EXCEPTION, labelKey: 'wms.sorterPlan.chuteType.exception' },
  { value: ChuteType.DISABLED, labelKey: 'wms.sorterPlan.chuteType.disabled' },
];

/**
 * 尾程商选项接口
 */
export interface CarrierOption {
  value: number;
  label: string;
  /** TT 包牌三位缩写（来自后端 TT_BAGGING_SORTING_RULE_ALIASES），未配置时为 null */
  abbreviation: string | null;
}

/**
 * 获取格口类型名称
 */
export const getChuteTypeName = (type: number, t: any): string => {
  const typeMap: {[key: number]: string} = {
    [ChuteType.NORMAL]: t('wms.sorterPlan.chuteType.normal'),
    [ChuteType.EXCEPTION]: t('wms.sorterPlan.chuteType.exception'),
    [ChuteType.DISABLED]: t('wms.sorterPlan.chuteType.disabled'),
  };
  return typeMap[type] || t('wms.sorterPlan.chuteType.unknown');
};

/**
 * 业务类型选项（不使用国际化）
 */
export const businessTypeOptions = [
  { value: BusinessType.SWIFTX, label: 'SwiftX' },
  { value: BusinessType.TT, label: 'TT' },
  { value: BusinessType.TEMU, label: 'TEMU' },
];

/**
 * 获取业务类型名称（不使用国际化）
 */
export const getBusinessTypeName = (type: number | null | undefined): string => {
  if (type === null || type === undefined) return '--';
  const typeMap: {[key: number]: string} = {
    [BusinessType.SWIFTX]: 'SwiftX',
    [BusinessType.TT]: 'TT',
    [BusinessType.TEMU]: 'TEMU',
  };
  return typeMap[type] || '--';
};

/**
 * 打印类型选项
 */
export const printTypeOptions = [
  { value: PrintType.SWIFTX, label: 'SWX' },
  { value: PrintType.TT, label: 'TT' },
];

/**
 * 获取打印类型名称
 */
export const getPrintTypeName = (type: number | null | undefined): string => {
  if (type === null || type === undefined) return '--';
  const typeMap: {[key: number]: string} = {
    [PrintType.SWIFTX]: 'SWX',
    [PrintType.TT]: 'TT',
  };
  return typeMap[type] || '--';
};

/**
 * 获取尾程商名称（用于显示从后端获取的数据）
 *
 * @param code 尾程商编码
 * @param carrierOptions 从后端获取的尾程商选项列表（含 abbreviation）
 * @param useAbbreviation 是否使用缩写，默认 false；缩写来源：后端 FinalMileCarrier.TT_BAGGING_SORTING_RULE_ALIASES
 *                       后端未配置缩写时回退使用 label（原承运商名）
 */
export const getFinalMileCarrierName = (
  code: number | null | undefined,
  carrierOptions?: CarrierOption[],
  useAbbreviation: boolean = false
): string => {
  if (code === null || code === undefined) {
    return '--';
  }

  const carrier = carrierOptions?.find(option => option.value === code);
  if (!carrier) {
    return '--';
  }

  return useAbbreviation ? (carrier.abbreviation || carrier.label) : carrier.label;
};

